import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
  });

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      } else {
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success("Account created successfully!");
      }
      navigate("/");
    } catch (error) {
      console.error("Auth error:", error);
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 flex items-center" data-testid="auth-page">
      <div className="max-w-[1400px] mx-auto w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Branding */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block"
          >
            <div className="flex items-center gap-4 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_tank-fashion/artifacts/rut5gr1k_igor-final.png" 
                alt="3T Logo" 
                className="h-16 w-auto"
              />
            </div>
            <p className="font-mono text-[#FFD700] text-sm tracking-[0.3em] mb-4">
              JOIN THE COMMUNITY
            </p>
            <h1 className="font-heading text-6xl xl:text-8xl tracking-tight uppercase leading-[0.9]">
              YOUR<br />
              <span className="text-[#FFD700]">STYLE</span><br />
              AWAITS
            </h1>
            <p className="text-[#A3A3A3] text-lg mt-8 max-w-md">
              Create an account to save your favorites, track orders, and get exclusive access to new drops.
            </p>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="border border-white/10 p-8 md:p-12">
              {/* Toggle */}
              <div className="flex border-b border-white/10 mb-8">
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 pb-4 font-heading text-xl tracking-wider transition-colors ${
                    isLogin ? "text-[#FFD700] border-b-2 border-[#FFD700]" : "text-[#A3A3A3]"
                  }`}
                  data-testid="login-tab"
                >
                  LOGIN
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 pb-4 font-heading text-xl tracking-wider transition-colors ${
                    !isLogin ? "text-[#FFD700] border-b-2 border-[#FFD700]" : "text-[#A3A3A3]"
                  }`}
                  data-testid="register-tab"
                >
                  REGISTER
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Field (Register only) */}
                {!isLogin && (
                  <div className="relative flex items-center">
                    <User className="absolute left-0 w-5 h-5 text-[#A3A3A3]" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-transparent border-b-2 border-white/20 py-4 pl-10 pr-4 text-lg text-white placeholder-white/30 outline-none focus:border-[#FFD700] transition-colors"
                      data-testid="name-input"
                    />
                  </div>
                )}

                {/* Email Field */}
                <div className="relative flex items-center">
                  <Mail className="absolute left-0 w-5 h-5 text-[#A3A3A3]" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-transparent border-b-2 border-white/20 py-4 pl-10 pr-4 text-lg text-white placeholder-white/30 outline-none focus:border-[#FFD700] transition-colors"
                    required
                    data-testid="email-input"
                  />
                </div>

                {/* Password Field */}
                <div className="relative flex items-center">
                  <Lock className="absolute left-0 w-5 h-5 text-[#A3A3A3]" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-transparent border-b-2 border-white/20 py-4 pl-10 pr-4 text-lg text-white placeholder-white/30 outline-none focus:border-[#FFD700] transition-colors"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-3 mt-8"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <div className="loader w-5 h-5 border-2" />
                  ) : (
                    <>
                      {isLogin ? "Login" : "Create Account"}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              {/* Forgot Password (Login only) */}
              {isLogin && (
                <Link
                  to="/forgot-password"
                  className="block text-right text-[#A3A3A3] hover:text-[#FFD700] font-mono text-xs tracking-wider transition-colors mt-2"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              )}

              {/* Back to shop */}
              <Link
                to="/shop"
                className="block text-center text-[#A3A3A3] hover:text-white mt-8 font-mono text-sm tracking-wider transition-colors"
                data-testid="back-to-shop"
              >
                Continue as Guest
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
